import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Trophy } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHallOfFame } from "@/hooks/useHallOfFame";
import { cn } from "@/lib/utils";

const NavbarMenu: React.FC = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const { currentUserRank } = useHallOfFame();

  const currentRole = profile?.role || "student";
  const isActive = (path: string) => location.pathname === path;
  const isTopThree = currentUserRank !== null && currentUserRank <= 3;

  return (
    <nav className="hidden md:flex items-center space-x-6">
      {/* Hlavní navigace pro žáky */}
      {currentRole === "student" && (
        <>
          <Link to="/">
            <Button variant={isActive("/") ? "default" : "ghost"} size="sm">
              {t("Přehled", "Prehľad")}
            </Button>
          </Link>
          <Link to="/tutor">
            <Button variant={isActive("/tutor") ? "default" : "ghost"} size="sm">
              AI Tutor
            </Button>
          </Link>
          <Link to="/challenges">
            <Button variant={isActive("/challenges") ? "default" : "ghost"} size="sm">
              {t("Výzvy a úkoly", "Výzvy a úlohy")}
            </Button>
          </Link>
          <Link to="/ideas">
            <Button variant={isActive("/ideas") ? "default" : "ghost"} size="sm">
              Moje nápady
            </Button>
          </Link>
          <Link to="/achievements">
            <Button variant={isActive("/achievements") ? "default" : "ghost"} size="sm">
              {t("Moje úspěchy", "Moje úspechy")}
            </Button>
          </Link>
          <Link to="/my-class">
            <Button variant={isActive("/my-class") ? "default" : "ghost"} size="sm">
              {t("Moje třída", "Moja trieda")}
            </Button>
          </Link>
        </>
      )}

      {/* Navigace pro učitele */}
      {currentRole === "teacher" && (
        <>
          <Link to="/teacher/classes">
            <Button variant={isActive("/teacher/classes") ? "default" : "ghost"} size="sm">
              {t("Třídy", "Triedy")}
            </Button>
          </Link>
          <Link to="/teacher/challenges">
            <Button variant={isActive("/teacher/challenges") ? "default" : "ghost"} size="sm">
              {t("Výzvy a úkoly", "Výzvy a úlohy")}
            </Button>
          </Link>
          <Link to="/teacher/inspiration">
            <Button variant={isActive("/teacher/inspiration") ? "default" : "ghost"} size="sm">
              {t("Inspirace", "Inšpirácie")}
            </Button>
          </Link>
        </>
      )}

      {/* Navigace pro administrátory */}
      {currentRole === "admin" && (
        <>
          <Link to="/admin/users">
            <Button variant={isActive("/admin/users") ? "default" : "ghost"} size="sm">
              {t("Uživatelé", "Používatelia")}
            </Button>
          </Link>
          <Link to="/admin/testing">
            <Button variant={isActive("/admin/testing") ? "default" : "ghost"} size="sm">
              {t("Testování", "Testovanie")}
            </Button>
          </Link>
          <Link to="/admin/safety">
            <Button variant={isActive("/admin/safety") ? "default" : "ghost"} size="sm">
              {t("Bezpečnost", "Bezpečnosť")}
            </Button>
          </Link>
          <Link to="/admin/stats">
            <Button variant={isActive("/admin/stats") ? "default" : "ghost"} size="sm">
              {t("Statistiky", "Štatistiky")}
            </Button>
          </Link>
        </>
      )}

      <Link to="/hall-of-fame">
        <Button
          variant={isActive("/hall-of-fame") && !isTopThree ? "default" : "ghost"}
          size="sm"
          className={cn(
            isTopThree &&
              "border border-yellow-500/60 bg-gradient-to-r from-yellow-500/15 via-amber-400/10 to-yellow-500/15 text-yellow-600 dark:text-yellow-400 hover:from-yellow-500/25 hover:via-amber-400/20 hover:to-yellow-500/25",
            isTopThree &&
              isActive("/hall-of-fame") &&
              "from-yellow-500/30 via-amber-400/25 to-yellow-500/30 shadow-sm shadow-yellow-500/20"
          )}
        >
          {isTopThree && <Trophy className="h-3.5 w-3.5 mr-1.5" />}
          {t("Síň slávy", "Sieň slávy")}
        </Button>
      </Link>
    </nav>
  );
};

export default NavbarMenu;
